import { get, has, isArray, isEmpty, isObject, isUndefined, transform } from 'lodash';

/**
 * @name pureObject
 * @description 递归清理对象中的空值字段
 */
export function pureObject(obj?: Record<string, any>): any {
  // 如果不是对象，直接返回
  if (!isObject(obj)) return obj;

  return transform(
    obj,
    (result, value, key) => {
      // 忽略值为 undefined 的字段
      if (isUndefined(value)) return;

      // 递归处理嵌套对象
      if (isObject(value) && !isArray(value)) {
        const nested = pureObject(value);
        // 仅保留非空对象
        if (!isEmpty(nested)) result[key] = nested;
      } else {
        result[key] = value;
      }
    },
    {} as Record<string, any>,
  );
}

/**
 * @name objectKeyProbe 对象 key 探针
 * @description 探测对象中是否存在指定路径的键
 * @param obj 对象
 * @param path 路径
 * @returns {[boolean, any]} [isExist, value] 否存在 , 对应的值
 * @example
 * const obj = {
 *   type: "secretKeyRef",
 *   valueFrom: {
 *     secretKeyRef: {
 *       name: "1",
 *       value: "123",
 *       undef: undefined,
 *       empty: null,
 *     },
 *   },
 *   name: "22",
 * };
console.log(objectKeyProbe(obj)); // [true, obj]
console.log(objectKeyProbe({})); // [true, {}]
console.log(objectKeyProbe(obj, "valueFrom.secretKeyRef")); // [true, { name: "1", value: "123" } ]
console.log(objectKeyProbe(obj, "valueFrom.secretKeyRef.value")); // [true, 123]
console.log(objectKeyProbe(obj, "valueFrom.secretKeyRef.name")); // [true, 1]
console.log(objectKeyProbe(obj, "name")); // [true, 22]
console.log(objectKeyProbe(obj, "valueFrom.secretKeyRef.undef")); // [true, undefined]
console.log(objectKeyProbe(obj, "valueFrom.secretKeyRef.empty")); // [true, null]
console.log(objectKeyProbe(obj, "valueFrom.jack")); // [false, null]
console.log(objectKeyProbe(undefined)); // [false, null]
console.log(objectKeyProbe(null)); // [false, null]
console.log(objectKeyProbe([])); // [false, null]
console.log(objectKeyProbe(() => {})); // [false, null]
 */
export function objectKeyProbe(obj: any, path?: string): [boolean, any] {
  const objectType = Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
  const isObject = objectType === 'object';
  // 不是对象或者不存在键的情况都是 [false, null]
  if (!isObject) return [false, null];
  // 处理路径未传或为空的情况，直接返回整个对象
  if (!path) {
    return [true, obj];
  }

  const isExist = has(obj, path);
  const targetValue = isExist ? get(obj, path) : null;

  return [isExist, targetValue];
}

/**
 * @name objectToKeyValueString 对象转键值字符串
 * @description 将对象转换为键值字符串, 用指定分隔符分隔（默认逗号）
 * @example
 * const data = { a: 1, b: 'hello', c: true };
 * console.log(objectToKeyValueString(data)); // 输出 "a=1,b=hello,c=true"
 * console.log(objectToKeyValueString(data, { separator: ';' })); // 输出 "a=1;b=hello;c=true"
 */
export function objectToKeyValueString(
  obj: any,
  options: { separator?: string } = {},
): string | undefined {
  const { separator = ',' } = options;
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return undefined;
  }
  return Object.entries(obj)
    .map(([key, value]) => `${key}=${value}`)
    .join(separator);
}
